/*!
 * sequence.js — the running chain of cues, and a hand on it
 * © 2025 Rob Canning — GPLv3
 *
 * A "sequence" is what cues set going by themselves once started: a next:
 * hand-over (cueNext.js), a tdelay: waiting to fire (cueDispatcher.js), a
 * zoom() moving the view (zoom.js). This module holds all three so they can be
 *
 *   paused   — hand-overs and delayed cues wait in a queue; a moving zoom
 *              stops where it is and will finish its move on resume
 *   resumed  — the queue runs, in order
 *   stopped  — everything pending is dropped and a moving zoom is cancelled
 *              (resetAllAnimations calls this: a rewind or reset ends it)
 *
 * A clock (addClock) is something that keeps its own time — a map-mode route
 * cursor (route.js). Paused, it stops counting, so what it times is held, not
 * just delayed; stopped, it ends.
 *
 * The performer's own navigation wins: dragging the score, the wheel, or a zoom
 * that zoom() did not make (toolbar, pinch, keys) pauses the sequence, so the
 * view is theirs until they press resume. A plain click (a button, a toggle)
 * does not count.
 *
 * Playback is not touched: the playhead and the cues it fires carry on.
 * While a sequence is active a small button offers pause / resume.
 */

let paused = false;
const queue = [];            // actions held while paused, in order
const timers = new Set();    // pending tdelay timers
let activeZoom = null;       // { interrupt(): fn-to-resume }
const clocks = new Set();    // { pause(), resume(), stop() } — see addClock
let button = null;

function busy() {
    return paused || queue.length > 0 || timers.size > 0 || !!activeZoom || clocks.size > 0;
}

// ── the button ────────────────────────────────────────────────────────
function ensureButton() {
    if (button || typeof document === "undefined" || !document.body) return button;
    button = document.createElement("button");
    button.id = "oscilla-sequence-button";
    button.type = "button";
    button.style.cssText = [
        "position:fixed", "left:12px", "bottom:64px", "z-index:100020",
        "padding:6px 12px", "border-radius:6px", "border:1px solid #bbb",
        "background:#f8f8f8", "color:#333", "font:13px sans-serif",
        "box-shadow:0 1px 4px rgba(0,0,0,.15)", "cursor:pointer", "display:none",
    ].join(";");
    button.addEventListener("pointerdown", (e) => e.stopPropagation());
    button.addEventListener("click", (e) => {
        e.stopPropagation();
        if (paused) resume(); else pause("button");
    });
    document.body.appendChild(button);
    return button;
}

function refresh() {
    const b = ensureButton();
    if (!b) return;
    // map mode: the transport bar pauses and resumes the route (mapTransport.js)
    b.style.display = busy() && !window.mapMode ? "block" : "none";
    b.textContent = paused ? "▶ resume sequence" : "⏸ pause sequence";
    b.title = paused
        ? "The sequence is holding (you took the view). Resume to carry on."
        : "Hold the chained cues (next:, delays, zoom moves) — playback is not affected";
}

// ── state changes ─────────────────────────────────────────────────────
export function pause(reason = "") {
    if (paused) return;
    paused = true;
    if (activeZoom) {
        const resumeMove = activeZoom.interrupt();
        activeZoom = null;
        if (resumeMove) queue.unshift(resumeMove);
    }
    for (const c of clocks) c.pause();
    console.log(`[sequence] paused${reason ? ` (${reason})` : ""}`);
    refresh();
}

export function resume() {
    if (!paused) return;
    paused = false;
    console.log(`[sequence] resumed (${queue.length} waiting)`);
    for (const c of clocks) c.resume();
    const run = queue.splice(0);
    for (const fn of run) {
        try { fn(); } catch (err) { console.warn("[sequence] step failed:", err); }
    }
    refresh();
}

export function stop() {
    paused = false;
    queue.length = 0;
    for (const t of timers) clearTimeout(t);
    timers.clear();
    if (activeZoom) { activeZoom.interrupt(); activeZoom = null; }
    const running = [...clocks];
    clocks.clear();
    for (const c of running) c.stop();
    refresh();
}

export function isPaused() { return paused; }

// ── entry points for the cue modules ──────────────────────────────────
/** A chained step (next:): runs now, or waits while paused. */
export function step(fn) {
    if (paused) { queue.push(fn); refresh(); return; }
    fn();
}

/** A delayed step (tdelay:): tracked so stop() can drop it; waits if paused when due. */
export function later(fn, ms) {
    const t = setTimeout(() => {
        timers.delete(t);
        step(fn);
        refresh();
    }, ms);
    timers.add(t);
    refresh();
    return t;
}

/** Call off one delayed step (a cue started again before its tdelay ran out). */
export function cancelLater(t) {
    if (!timers.delete(t)) return;
    clearTimeout(t);
    refresh();
}

/** A clock joins the sequence: paused, resumed and stopped with it. */
export function addClock(clock) {
    clocks.add(clock);
    if (paused) clock.pause();
    refresh();
}
export function removeClock(clock) {
    clocks.delete(clock);
    refresh();
}

/** zoom() registers its move; interrupt() stops it and returns how to finish it. */
export function zoomStarted(handle) {
    activeZoom = handle;
    refresh();
}
export function zoomEnded(handle) {
    if (activeZoom === handle) activeZoom = null;
    refresh();
}

// ── the performer's navigation ────────────────────────────────────────
// zoom() records the levels it sets, so a zoom-changed event it caused is not
// mistaken for the performer's
const ownZooms = [];
export function noteOwnZoom(z) {
    ownZooms.push(z);
    if (ownZooms.length > 6) ownZooms.shift();
}

function manual(reason) {
    if (!busy() || paused) return;
    pause(reason);
}

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("oscilla:zoom-changed", (e) => {
        const z = e.detail?.zoom;
        if (z == null || ownZooms.some(v => Math.abs(v - z) < 1e-6)) return;
        manual("manual zoom");
    });

    // A drag on the score (pan, scrub) — not a click
    let down = null;
    // controls laid over the score (panels, sliders, buttons) are not navigation
    const CONTROLS = "#oscilla-sequence-button, .oscilla-drag-overlay, .oscilla-drag-click-overlay, "
        + ".oscilla-click-overlay, .oscilla-button-overlay, .oscilla-hit, .o2p-control-bar, "
        + ".o2p-launcher, .cue-text-overlay, .cue-text-scroll-container";
    const inScore = (t) => !!t?.closest?.("#scoreContainer") && !t.closest(CONTROLS);
    window.addEventListener("pointerdown", (e) => {
        down = inScore(e.target) ? { x: e.clientX, y: e.clientY } : null;
    }, true);
    window.addEventListener("pointermove", (e) => {
        if (!down) return;
        if (Math.hypot(e.clientX - down.x, e.clientY - down.y) > 6) { down = null; manual("drag"); }
    }, true);
    window.addEventListener("pointerup", () => { down = null; }, true);
    window.addEventListener("wheel", (e) => { if (inScore(e.target)) manual("wheel"); }, { capture: true, passive: true });

    window.oscillaSequence = { pause, resume, stop, isPaused, busy };
}
